import React from "react"
import { 
  Clock, BadgeCheck, ChevronRight, Package, Layers, 
  Building2, FileCheck2, Calendar, Tag, Hash, FileText, MapPin, Receipt 
} from "lucide-react"
import { cn } from "@/lib/utils"
import { resolveTargetSummary, TargetChip } from "./RfqCard"

// Helper to format date
function formatDate(isoStr: string | null | undefined) {
  if (!isoStr) return "-"
  const d = new Date(isoStr)
  if (isNaN(d.getTime())) return "-"
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

const STATUS_STYLES: Record<string, string> = {
  APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-100",
  PENDING: "bg-amber-50 text-amber-700 border-amber-100",
  REJECTED: "bg-red-50 text-red-700 border-red-100",
}

export interface QuotationCardProps {
  quotation: any
  role: "buyer" | "seller" | "admin"
  onSelect: (quotation: any) => void
  activeTab?: "buyer" | "seller" // Used in admin role to toggle tab perspectives
}

export function QuotationCard({ quotation, role, onSelect, activeTab }: QuotationCardProps) {
  const statusClass = STATUS_STYLES[quotation.status] || "bg-slate-50 text-slate-700 border-slate-200"

  const rfqData = quotation.rfq || quotation.adminToSellerRfq || quotation.buyerToAdminRfq || null
  const targets = rfqData?.targets || []
  const chips = (role !== "seller" && role !== "admin") ? resolveTargetSummary(targets) : []
  const visibleChips = chips.slice(0, 3)
  const overflowCount = chips.length - visibleChips.length

  // Date Label
  const dateLabel = role === "seller" ? "Sent" : role === "admin" ? (activeTab === "seller" ? "Received" : "Sent") : "Received"

  // Specs/Key details
  const companyName = role !== "seller" ? (quotation.company?.name || null) : null
  const companyLogo = role !== "seller" ? (quotation.company?.logoUrl || null) : null
  const buyerName = quotation.buyer?.user?.name || rfqData?.buyer?.user?.name || rfqData?.rfq?.buyer?.user?.name || null
  const buyerLogo = quotation.buyer?.user?.profileImageUrl || rfqData?.buyer?.user?.profileImageUrl || rfqData?.rfq?.buyer?.user?.profileImageUrl || null

  const showBuyerHeader = ((role === "admin" && activeTab === "buyer") || role === "seller") && !!buyerName
  
  // Primary CTA label
  let ctaText = "Details"
  if (role === "admin" && activeTab === "seller" && quotation.status === "PENDING") {
    if ((quotation.FORWARD_COUNT || 0) > 0) {
      ctaText = "Details"
    } else {
      ctaText = "Forward to Buyer"
    }
  } else if (role === "buyer" && quotation.status === "APPROVED") {
    ctaText = "Place Order"
  }

  return (
    <div
      onClick={() => onSelect(quotation)}
      className="group cursor-pointer rounded-2xl border-[0.5px] border-slate-200/70 bg-white p-4 shadow-sm transition-all hover:border-[#be001e33] hover:shadow-[0_10px_30px_rgba(230,32,47,0.05)] text-left"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {showBuyerHeader ? (
            <div className="flex items-center gap-2 text-sm font-extrabold text-slate-800">
              <div className="flex size-6 shrink-0 items-center justify-center rounded bg-slate-100 border border-slate-200">
                {buyerLogo ? (
                  <img
                    src={buyerLogo}
                    alt={buyerName}
                    className="size-4 object-contain"
                  />
                ) : (
                  <Building2 className="size-3.5 text-[#e6202f]" />
                )}
              </div>
              <span className="text-slate-600 font-bold text-xs shrink-0">
                <span className="hidden sm:inline">Buyer: </span>{buyerName}
              </span>
              <span className="text-slate-300 font-normal">|</span>
              <span className="truncate font-bold text-slate-800 group-hover:text-[#be001e] transition-colors">
                {quotation.message || rfqData?.queryMessage || "Sourcing Inquiry"}
              </span>
            </div>
          ) : companyName && role !== "seller" ? (
            <div className="flex items-center gap-2 text-sm font-extrabold text-slate-800">
              <div className="flex size-6 shrink-0 items-center justify-center rounded bg-slate-100 border border-slate-200">
                {companyLogo ? (
                  <img
                    src={companyLogo}
                    alt={companyName}
                    className="size-4 object-contain"
                  />
                ) : (
                  <Building2 className="size-3.5 text-[#e6202f]" />
                )}
              </div>
              <span className="text-slate-600 font-bold text-xs shrink-0">
                <span className="hidden sm:inline">Company: </span>{companyName}
              </span>
              <span className="text-slate-300 font-normal">|</span>
              <span className="truncate font-bold text-slate-800 group-hover:text-[#be001e] transition-colors">
                {quotation.message || rfqData?.queryMessage || "Sourcing Inquiry"}
              </span>
            </div>
          ) : (
            <h3 className="truncate text-sm font-bold text-slate-800 group-hover:text-[#be001e] transition-colors leading-tight">
              {quotation.message || rfqData?.queryMessage || "Quotation details"}
            </h3>
          )}
        </div>
        <span
          className={cn(
            "shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider",
            statusClass
          )}
        >
          {quotation.status}
        </span>
      </div>

      {/* Grid of details */}
      <div className="mt-3.5 grid grid-cols-2 sm:grid-cols-3 gap-4 border-t border-slate-100 pt-3">
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{dateLabel} Date</p>
          <p className="text-xs font-bold text-slate-700 mt-0.5">{formatDate(quotation.createdAt)}</p>
        </div>
        <div>
          <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Total Value</p>
          <p className="text-xs font-black text-[#be001e] mt-0.5 font-mono">
            {quotation.currencyCode} {Number(quotation.totalAmount || 0).toFixed(2)}
          </p>
        </div>
      </div>

      {/* Target chips row (if any) - hidden on seller and admin panels */}
      {role !== "seller" && role !== "admin" && chips.length > 0 && (
        <div className="mt-3.5 border-t border-slate-100 pt-3">
          <p className="mb-1.5 text-[9px] font-bold uppercase tracking-wider text-slate-400">
            Sourcing Target{chips.length === 1 ? "" : "s"}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {visibleChips.map((c) => (
              <TargetChip key={c.key} data={c} />
            ))}
            {overflowCount > 0 && (
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-500">
                +{overflowCount} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Footer / CTA row */}
      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
        <span className="text-[11px] font-medium text-slate-400">
          Last Updated: {formatDate(quotation.updatedAt)}
        </span>
        <button
          type="button"
          className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#be001e] group-hover:text-[#e6202f] transition-all"
        >
          {ctaText}
          <ChevronRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  )
}

export function QuotationCardSkeleton() {
  return (
    <div className="rounded-2xl border-[0.5px] border-slate-200/70 bg-white p-4 shadow-sm animate-pulse text-left">
      {/* Header Skeleton */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="size-6 bg-slate-200 rounded shrink-0" />
          <div className="h-3.5 bg-slate-200 rounded w-24 shrink-0" />
          <div className="h-3.5 bg-slate-200 rounded w-48 truncate" />
        </div>
        <div className="h-6 bg-slate-200 rounded-full w-20 shrink-0" />
      </div>

      {/* Grid of details */}
      <div className="mt-3.5 grid grid-cols-2 sm:grid-cols-3 gap-4 border-t border-slate-100 pt-3">
        <div>
          <div className="h-2.5 bg-slate-200 rounded w-16 mb-1.5" />
          <div className="h-4 bg-slate-200 rounded w-24" />
        </div>
        <div>
          <div className="h-2.5 bg-slate-200 rounded w-16 mb-1.5" />
          <div className="h-4 bg-slate-200 rounded w-28" />
        </div>
      </div>

      {/* Target Chips Row Skeleton */}
      <div className="mt-3.5 border-t border-slate-100 pt-3">
        <div className="h-2.5 bg-slate-200 rounded w-24 mb-2" />
        <div className="flex flex-wrap gap-1.5">
          <div className="h-6 bg-slate-200 rounded-full w-28" />
          <div className="h-6 bg-slate-200 rounded-full w-32" />
        </div>
      </div>

      {/* Footer CTA Skeleton */}
      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3">
        <div className="h-3 bg-slate-200 rounded w-36" />
        <div className="h-4 bg-slate-200 rounded w-20" />
      </div>
    </div>
  )
}
